import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEventMode } from "@/context/EventModeContext";
import useLocale from "@/hooks/useLocale";
import { formatCountdown, formatEventDate, isSoloEvent } from "@/lib/events";
import { ClockState, EventMineRead, EventState } from "@openapi";
import {
    ClockCircleOutlined,
    PlusOutlined,
    TrophyOutlined,
} from "@ant-design/icons";
import { Button, Card, Tag, theme, Typography } from "antd";
import type { TFunction } from "i18next";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EventPickerModal from "./EventPickerModal";

/** Height of the strip, so the page can size the content below it. */
export const EVENT_CARDS_HEIGHT = 92;

const CARD_WIDTH = 250;
// Same threshold as the event banner turning into a warning.
const LAST_MINUTES_SECONDS = 5 * 60;

const CLOCK_COLORS: Record<ClockState, string> = {
    [ClockState.Waiting]: "gold",
    [ClockState.Running]: "green",
    [ClockState.Finished]: "default",
};

// Running events first, then waiting ones by start, finished last.
const order = (e: EventMineRead) =>
    e.clock_state === ClockState.Running
        ? 0
        : e.clock_state === ClockState.Waiting
          ? 1
          : 2;

// The team after the competition name in a card's subtitle. A nameless solo
// participant has no team to speak of, so nothing is added.
const teamSuffix = (e: EventMineRead, t: TFunction): string => {
    const name =
        e.competitor_name ||
        (isSoloEvent(e) ? null : t("events.mode.unnamedTeam"));
    return name ? ` · ${name}` : "";
};

interface EventCardsProps {
    className?: string;
}

// Small cards with the student's current competitions above the home page
// content: state, countdown, and a quick way into event mode.
export default function EventCards({ className }: EventCardsProps) {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const { isLoggedIn } = useAuth();
    const { eventId, enterEvent } = useEventMode();
    const locale = useLocale();
    const router = useRouter();
    const openDetails = (id: string) => router.push(`/${locale}/events/${id}`);
    const [events, setEvents] = useState<EventMineRead[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [now, setNow] = useState(() => Date.now());

    const load = useCallback(async () => {
        try {
            const mine = await api.events.listMyEvents();
            setEvents(
                mine
                    .filter((e) => e.state !== EventState.Closed)
                    .sort(
                        (a, b) =>
                            order(a) - order(b) ||
                            a.available_start.getTime() -
                                b.available_start.getTime(),
                    ),
            );
        } catch {
            // The strip is a convenience; the picker shows errors properly.
            setEvents([]);
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn) return;
        const timeout = setTimeout(() => void load(), 0);
        return () => clearTimeout(timeout);
    }, [isLoggedIn, load]);

    const anyRunning = events.some(
        (e) => e.clock_state === ClockState.Running && e.deadline,
    );
    useEffect(() => {
        if (!anyRunning) return;
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [anyRunning]);

    if (!isLoggedIn) return null;

    const statusLine = (e: EventMineRead) => {
        if (e.clock_state === ClockState.Running && e.deadline) {
            const left = Math.ceil((e.deadline.getTime() - now) / 1000);
            if (left > 0) {
                return (
                    <>
                        <ClockCircleOutlined />{" "}
                        <Typography.Text
                            strong
                            type={
                                left <= LAST_MINUTES_SECONDS
                                    ? "warning"
                                    : undefined
                            }
                            style={{ fontVariantNumeric: "tabular-nums" }}
                        >
                            {formatCountdown(left)}
                        </Typography.Text>{" "}
                        {t("events.mode.timeLeft")}
                    </>
                );
            }
            return t("events.clock.finished");
        }
        if (e.clock_state === ClockState.Waiting) {
            return e.organization_id
                ? t("events.cards.waitingForOrganization")
                : t("events.cards.startsAt", {
                      start: formatEventDate(e.available_start, locale),
                  });
        }
        return t("events.clock.finished");
    };

    const cardStyle: React.CSSProperties = {
        width: CARD_WIDTH,
        flex: "0 0 auto",
        height: EVENT_CARDS_HEIGHT - 12,
    };

    return (
        <div
            className={className}
            style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                height: EVENT_CARDS_HEIGHT,
                padding: "6px 3px",
                boxSizing: "border-box",
            }}
        >
            {events.map((e) => {
                const isCurrent = e.id === eventId;
                return (
                    <Card
                        key={e.id}
                        size="small"
                        style={{
                            ...cardStyle,
                            border: `1px solid ${
                                isCurrent
                                    ? token.colorSuccess
                                    : token.colorBorderSecondary
                            }`,
                        }}
                        styles={{ body: { padding: "6px 10px" } }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            <TrophyOutlined
                                style={{ color: token.colorWarning }}
                            />
                            <Typography.Text
                                strong
                                ellipsis={{ tooltip: e.name }}
                                style={{ flex: 1, minWidth: 0 }}
                            >
                                {e.name}
                            </Typography.Text>
                            <Tag
                                color={CLOCK_COLORS[e.clock_state]}
                                style={{ marginInlineEnd: 0 }}
                            >
                                {t(`events.clock.${e.clock_state}`)}
                            </Tag>
                        </div>
                        <Typography.Text
                            type="secondary"
                            ellipsis
                            style={{ display: "block", fontSize: 12 }}
                        >
                            {e.competition_name}
                            {teamSuffix(e, t)}
                        </Typography.Text>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 6,
                                fontSize: 12,
                            }}
                        >
                            <span
                                style={{
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {statusLine(e)}
                            </span>
                            <Button
                                size="small"
                                type="primary"
                                onClick={() => {
                                    if (!isCurrent) enterEvent(e.id);
                                    openDetails(e.id);
                                }}
                            >
                                {isCurrent
                                    ? t("events.details.open")
                                    : t("events.picker.select")}
                            </Button>
                        </div>
                    </Card>
                );
            })}
            <Card
                size="small"
                hoverable
                onClick={() => setPickerOpen(true)}
                style={{
                    ...cardStyle,
                    width: events.length ? 170 : CARD_WIDTH,
                    border: `1px dashed ${token.colorBorder}`,
                }}
                styles={{
                    body: {
                        height: "100%",
                        padding: "6px 10px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                    },
                }}
            >
                <Typography.Text strong>
                    <PlusOutlined /> {t("events.cards.browse")}
                </Typography.Text>
                {loaded && events.length === 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {t("events.cards.none")}
                    </Typography.Text>
                )}
            </Card>
            <EventPickerModal
                open={pickerOpen}
                onEntered={openDetails}
                onClose={() => {
                    setPickerOpen(false);
                    void load();
                }}
            />
        </div>
    );
}
